import {
  ApiError,
  type CreateApiClientOptions,
  createApiClient,
  rawRequest,
} from "@aurumiq/api-client";
import { createApiHooks } from "@aurumiq/api-client/query";
import { env } from "./env";

/**
 * The token getter is registered by <ApiTokenBridge/> once AuthProvider mounts,
 * so the client can be a module singleton (and so TanStack Query keys stay simple).
 */
let tokenGetter: CreateApiClientOptions["getToken"] = () => null;
export function setTokenGetter(fn: CreateApiClientOptions["getToken"]) {
  tokenGetter = fn;
}

const options: CreateApiClientOptions = {
  baseUrl: env.apiUrl,
  getToken: () => tokenGetter?.(),
};

/** Typed openapi-fetch client (paths from packages/api-client/openapi.json). */
export const api = createApiClient(options);

/** Typed TanStack Query hooks: `apiHooks.useQuery("get", "/healthz")`. */
export const apiHooks = createApiHooks(api);

/**
 * Untyped calls for admin endpoints not yet in the placeholder spec.
 * Replace with `api.GET(...)` as the API team regenerates `openapi.json`.
 */
function withBody<T>(method: string, path: string, body?: unknown, init?: RequestInit) {
  return rawRequest<T>(options, path, {
    ...init,
    method,
    headers: { "content-type": "application/json", ...(init?.headers as Record<string, string>) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export const rawApi = {
  get: <T>(path: string, init?: RequestInit) =>
    rawRequest<T>(options, path, { ...init, method: "GET" }),
  post: <T>(path: string, body?: unknown, init?: RequestInit) =>
    withBody<T>("POST", path, body, init),
  patch: <T>(path: string, body?: unknown, init?: RequestInit) =>
    withBody<T>("PATCH", path, body, init),
  put: <T>(path: string, body?: unknown, init?: RequestInit) =>
    withBody<T>("PUT", path, body, init),
  delete: <T>(path: string, body?: unknown, init?: RequestInit) =>
    withBody<T>("DELETE", path, body, init),
};

/** Build a query string, skipping empty / undefined values. */
export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

/** User-facing message for anything thrown by the API client. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.isNetwork) return "Cannot reach the API.";
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

export type { OrgSummary, Paginated } from "./types";
